import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useId, useState } from 'react';
import { create } from 'zustand';

import { Box } from '@bonusly/design-system';

import { Bizy } from './bizy';
import { useReduceBizyMotionPreference } from './bizy-motion-preferences';
import { BizyState } from './types';

export type BizyFlightAnchorName = 'chat' | 'navFallback' | 'giveBoxGauge';

type AnchorRegistry = Record<BizyFlightAnchorName, Record<string, HTMLElement>>;
type BizyFlightSourceName = BizyFlightAnchorName | 'offscreenRight';

type FlightPoint = {
	x: number;
	y: number;
};

type FlightRoute = 'toGiveBox' | 'toSource';
type FlightPhase = 'flying' | 'parked';

type BizyFlight = {
	id: number;
	route: FlightRoute;
	phase: FlightPhase;
	sourceAnchorName: BizyFlightSourceName;
	sourceAnchorId: string;
	destinationAnchorName: BizyFlightAnchorName;
	from: FlightPoint;
	to: FlightPoint;
	parkedState: BizyState;
	duration: number;
};

type BizyFlightStore = {
	anchors: AnchorRegistry;
	flight: BizyFlight | null;
	registerAnchor: (name: BizyFlightAnchorName, id: string, element: HTMLElement | null) => void;
	flyToGiveBox: (parkedState?: BizyState, options?: { source?: 'preferred' | 'offscreenRight' }) => boolean;
	flyBackToSource: () => boolean;
	setParkedState: (parkedState: BizyState) => void;
	markParked: (id: number) => void;
	clearFlight: (id?: number) => void;
};

type ShouldStartBizyFlightOptions = {
	isBizyEnabled: boolean;
	isDraftAssistDesktop: boolean;
	reduceBizyMotion: boolean;
	shouldReduceMotion: boolean;
};

type ShouldStartBizyReducedMotionEntranceOptions = ShouldStartBizyFlightOptions & {
	isBizyPanelOpen: boolean;
};

const BIZY_FLIGHT_SIZE = 80;
const BIZY_FLIGHT_Z_INDEX = 2000;
const BIZY_FLIGHT_PEEK_Z_INDEX = 1450;
const BIZY_PEEK_CLIP_INSET_PX = 4;
const BIZY_PEEK_MASK_EDGE_OFFSET = 44;
let flightId = 0;

const createAnchorRegistry = (): AnchorRegistry => ({
	chat: {},
	navFallback: {},
	giveBoxGauge: {},
});

export const getFlightPoint = (rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>): FlightPoint => ({
	x: rect.left + rect.width / 2 - BIZY_FLIGHT_SIZE / 2,
	y: rect.top + rect.height / 2 - BIZY_FLIGHT_SIZE / 2,
});

const getViewportWidth = (): number => (typeof window === 'undefined' ? 0 : window.innerWidth);

const getNextFlightId = (): number => {
	flightId += 1;
	return flightId;
};

export const getOffscreenRightFlightPoint = (reference: FlightPoint, viewportWidth = getViewportWidth()): FlightPoint => ({
	x: viewportWidth + BIZY_FLIGHT_SIZE,
	y: reference.y,
});

export const getFlightDirection = (from: FlightPoint, to: FlightPoint): Extract<BizyState, 'flyingLeft' | 'flyingRight'> => {
	return to.x < from.x ? 'flyingLeft' : 'flyingRight';
};

export const getFlightDuration = (from: FlightPoint, to: FlightPoint): number => {
	const distance = Math.hypot(to.x - from.x, to.y - from.y);
	return Math.min(Math.max(distance / 800, 0.55), 1.2);
};

export const getFlightArcPoint = (from: FlightPoint, to: FlightPoint): FlightPoint => {
	const distance = Math.hypot(to.x - from.x, to.y - from.y);
	const arcHeight = Math.min(Math.max(distance * 0.18, 24), 120);

	return {
		x: (from.x + to.x) / 2,
		y: Math.min(from.y, to.y) - arcHeight,
	};
};

export const getFlightCurvePoints = (from: FlightPoint, to: FlightPoint): FlightPoint[] => {
	const control = getFlightArcPoint(from, to);
	const steps = 8;

	return Array.from({ length: steps + 1 }, (_, index) => {
		const t = index / steps;
		const oneMinusT = 1 - t;

		return {
			x: oneMinusT * oneMinusT * from.x + 2 * oneMinusT * t * control.x + t * t * to.x,
			y: oneMinusT * oneMinusT * from.y + 2 * oneMinusT * t * control.y + t * t * to.y,
		};
	});
};

export const getGiveBoxPeekEntrancePoints = (restingPoint: FlightPoint) => ({
	peek: { x: restingPoint.x + BIZY_PEEK_MASK_EDGE_OFFSET - BIZY_PEEK_CLIP_INSET_PX, y: restingPoint.y + 4 },
	front: { x: restingPoint.x + 4, y: restingPoint.y + 2 },
	resting: restingPoint,
});

export const shouldStartBizyFlight = ({ isBizyEnabled, isDraftAssistDesktop, reduceBizyMotion, shouldReduceMotion }: ShouldStartBizyFlightOptions): boolean => {
	return isBizyEnabled && isDraftAssistDesktop && !reduceBizyMotion && !shouldReduceMotion;
};

export const shouldStartBizyReducedMotionEntrance = ({
	isBizyEnabled,
	isDraftAssistDesktop,
	isBizyPanelOpen,
	reduceBizyMotion,
	shouldReduceMotion,
}: ShouldStartBizyReducedMotionEntranceOptions): boolean => {
	return isBizyEnabled && isDraftAssistDesktop && !isBizyPanelOpen && (reduceBizyMotion || shouldReduceMotion);
};

export const shouldUseGiveBoxPeekEntrance = (flight: Pick<BizyFlight, 'route' | 'sourceAnchorName'> | null): boolean => {
	return flight?.route === 'toGiveBox' && flight.sourceAnchorName === 'offscreenRight';
};

const isUsableAnchor = (element: HTMLElement): boolean => {
	if (!element.isConnected) return false;
	const rect = element.getBoundingClientRect();
	if (rect.width <= 0 || rect.height <= 0) return false;

	let current: HTMLElement | null = element;
	while (current && current !== document.body) {
		const styles = window.getComputedStyle(current);
		if (styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0') return false;
		current = current.parentElement;
	}

	return true;
};

const getFirstUsableAnchor = (anchors: Record<string, HTMLElement>): HTMLElement | null => {
	return Object.values(anchors).find(isUsableAnchor) ?? null;
};

const getFirstUsableAnchorEntry = (anchors: Record<string, HTMLElement>): { id: string; element: HTMLElement } | null => {
	const entry = Object.entries(anchors).find(([, element]) => isUsableAnchor(element));
	if (!entry) return null;
	return { id: entry[0], element: entry[1] };
};

const getPreferredSourceAnchor = (anchors: AnchorRegistry): { name: BizyFlightAnchorName; id: string; element: HTMLElement } | null => {
	const chat = getFirstUsableAnchorEntry(anchors.chat);
	if (chat) return { name: 'chat', ...chat };

	const navFallback = getFirstUsableAnchorEntry(anchors.navFallback);
	if (navFallback) return { name: 'navFallback', ...navFallback };

	return null;
};

const getAnchorById = (anchors: AnchorRegistry, name: BizyFlightAnchorName, id: string): HTMLElement | null => {
	const element = anchors[name][id];
	if (!element?.isConnected) return null;
	return element;
};

const measureAnchor = (element: HTMLElement): FlightPoint => getFlightPoint(element.getBoundingClientRect());

const getReturnPoint = (flight: BizyFlight, anchors: AnchorRegistry, from: FlightPoint): FlightPoint | null => {
	if (flight.sourceAnchorName === 'offscreenRight') {
		return getOffscreenRightFlightPoint(from);
	}

	const source =
		getAnchorById(anchors, flight.sourceAnchorName, flight.sourceAnchorId) ?? getFirstUsableAnchor(anchors.chat) ?? getFirstUsableAnchor(anchors.navFallback);

	return source ? measureAnchor(source) : null;
};

export const useBizyFlightStore = create<BizyFlightStore>((set) => ({
	anchors: createAnchorRegistry(),
	flight: null,
	registerAnchor: (name, id, element) =>
		set((state) => {
			const anchors = { ...state.anchors, [name]: { ...state.anchors[name] } };
			if (element) {
				anchors[name][id] = element;
			} else {
				delete anchors[name][id];
			}
			return { anchors };
		}),
	flyToGiveBox: (parkedState = 'curious', options = {}) => {
		let didStart = false;
		set((state) => {
			const destination = getFirstUsableAnchor(state.anchors.giveBoxGauge);
			if (!destination) return {};

			const to = measureAnchor(destination);
			const preferredSource = options.source === 'offscreenRight' ? null : getPreferredSourceAnchor(state.anchors);
			const source = preferredSource
				? {
						name: preferredSource.name,
						id: preferredSource.id,
						from: measureAnchor(preferredSource.element),
					}
				: {
						name: 'offscreenRight' as const,
						id: 'offscreenRight',
						from: getOffscreenRightFlightPoint(to),
					};
			didStart = true;
			return {
				flight: {
					id: getNextFlightId(),
					route: 'toGiveBox',
					phase: 'flying',
					sourceAnchorName: source.name,
					sourceAnchorId: source.id,
					destinationAnchorName: 'giveBoxGauge',
					from: source.from,
					to,
					parkedState,
					duration: getFlightDuration(source.from, to),
				},
			};
		});
		return didStart;
	},
	flyBackToSource: () => {
		let didStart = false;
		set((state) => {
			const flight = state.flight;
			if (!flight) return {};

			const destination = getFirstUsableAnchor(state.anchors[flight.destinationAnchorName]);
			const from = destination ? measureAnchor(destination) : flight.to;
			const to = getReturnPoint(flight, state.anchors, from);
			if (!to) return { flight: null };

			didStart = true;
			return {
				flight: {
					...flight,
					id: getNextFlightId(),
					route: 'toSource',
					phase: 'flying',
					from,
					to,
					duration: getFlightDuration(from, to),
				},
			};
		});
		return didStart;
	},
	setParkedState: (parkedState) =>
		set((state) => {
			if (!state.flight) return {};
			return { flight: { ...state.flight, parkedState } };
		}),
	markParked: (id) =>
		set((state) => {
			if (!state.flight || state.flight.id !== id || state.flight.route !== 'toGiveBox' || state.flight.phase !== 'flying') return {};
			return { flight: { ...state.flight, phase: 'parked', from: state.flight.to } };
		}),
	clearFlight: (id) =>
		set((state) => {
			if (id != null && state.flight?.id !== id) return {};
			return { flight: null };
		}),
}));

export const useBizyFlightAnchor = (name: BizyFlightAnchorName) => {
	const id = useId();
	const registerAnchor = useBizyFlightStore((state) => state.registerAnchor);

	return useCallback(
		(element: HTMLElement | null) => {
			registerAnchor(name, id, element);
		},
		[id, name, registerAnchor]
	);
};

export const useIsBizyFlightAnchorHidden = (name: BizyFlightAnchorName): boolean => {
	return useBizyFlightStore((state) => {
		const flight = state.flight;
		if (name === 'navFallback') return !!flight;
		return !!flight && (flight.sourceAnchorName === name || flight.destinationAnchorName === name);
	});
};

export const BizyFlightOverlay = () => {
	const flight = useBizyFlightStore((state) => state.flight);
	const markParked = useBizyFlightStore((state) => state.markParked);
	const clearFlight = useBizyFlightStore((state) => state.clearFlight);
	const shouldReduceMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();

	if (!flight) return null;

	const isFlying = flight.phase === 'flying';
	const shouldReduceBizyMotion = !!shouldReduceMotion || reduceBizyMotion;

	if (shouldUseGiveBoxPeekEntrance(flight)) {
		return <BizyGiveBoxPeekEntrance flight={flight} shouldReduceMotion={shouldReduceBizyMotion} markParked={markParked} />;
	}

	const bizyState = isFlying ? getFlightDirection(flight.from, flight.to) : flight.parkedState;
	const curve = getFlightCurvePoints(flight.from, flight.to);
	const shouldAnimateFlight = isFlying && !shouldReduceBizyMotion;

	return (
		<Box position="fixed" inset={0} pointerEvents="none" zIndex={BIZY_FLIGHT_Z_INDEX}>
			<motion.div
				key={flight.id}
				initial={{ x: flight.from.x, y: flight.from.y }}
				animate={shouldAnimateFlight ? { x: curve.map((point) => point.x), y: curve.map((point) => point.y) } : { x: flight.to.x, y: flight.to.y }}
				transition={{ duration: shouldAnimateFlight ? flight.duration : 0, ease: 'linear' }}
				onAnimationComplete={() => {
					if (flight.route === 'toGiveBox') {
						markParked(flight.id);
					} else {
						clearFlight(flight.id);
					}
				}}
				style={{ position: 'absolute', width: BIZY_FLIGHT_SIZE, height: BIZY_FLIGHT_SIZE, willChange: 'transform' }}
			>
				<Bizy
					state={bizyState}
					size={BIZY_FLIGHT_SIZE}
					animations={isFlying ? { wingSpeed: 0.08, wingAmplitude: 0.8, wingMode: 'squishStretch' } : undefined}
				/>
			</motion.div>
		</Box>
	);
};

type PeekPhase = 'hidden' | 'peek' | 'wave' | 'front' | 'settle' | 'reducedMotion';

interface BizyGiveBoxPeekEntranceProps {
	flight: BizyFlight;
	shouldReduceMotion: boolean;
	markParked: (id: number) => void;
}

const getPeekEntranceAnimation = (phase: PeekPhase, points: ReturnType<typeof getGiveBoxPeekEntrancePoints>) => {
	switch (phase) {
		case 'hidden':
		case 'peek':
		case 'wave':
			return { x: points.peek.x, y: points.peek.y, opacity: 1, clipPath: `inset(0 0 0 ${BIZY_PEEK_CLIP_INSET_PX}px)` };
		case 'front':
			return { x: points.front.x, y: points.front.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
		case 'settle':
			return { x: points.resting.x, y: points.resting.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
		case 'reducedMotion':
			return { x: points.resting.x, y: points.resting.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
	}
};

const getPeekEntranceBizyAnimation = (phase: PeekPhase) => {
	switch (phase) {
		case 'hidden':
			return { x: -BIZY_FLIGHT_SIZE, rotate: 0 };
		case 'peek':
		case 'wave':
			return { x: 0, rotate: -12 };
		case 'front':
		case 'settle':
		case 'reducedMotion':
			return { x: 0, rotate: 0 };
	}
};

export const getPeekEntranceBizyState = (phase: PeekPhase, parkedState: BizyState): BizyState => {
	if (phase === 'wave') return 'waving';
	if (phase === 'settle' || phase === 'reducedMotion') return parkedState;
	return 'curious';
};

const getPeekEntranceTransition = (phase: PeekPhase) => {
	switch (phase) {
		case 'peek':
			return { duration: 0.42, ease: 'linear' as const };
		case 'front':
			return { duration: 0.32, ease: 'linear' as const };
		case 'settle':
			return { duration: 0.46, ease: 'linear' as const };
		case 'reducedMotion':
			return { duration: 0.2, ease: 'linear' as const };
		case 'hidden':
		case 'wave':
			return { duration: 0 };
	}
};

const BizyGiveBoxPeekEntrance = ({ flight, shouldReduceMotion, markParked }: BizyGiveBoxPeekEntranceProps) => {
	const [phase, setPhase] = useState<PeekPhase>(shouldReduceMotion ? 'reducedMotion' : 'hidden');
	const points = getGiveBoxPeekEntrancePoints(flight.to);

	useEffect(() => {
		if (shouldReduceMotion) {
			setPhase('reducedMotion');
			const timer = setTimeout(() => markParked(flight.id), 200);
			return () => clearTimeout(timer);
		}

		setPhase('hidden');
		const timers = [
			setTimeout(() => setPhase('peek'), 16),
			setTimeout(() => setPhase('wave'), 440),
			setTimeout(() => setPhase('front'), 960),
			setTimeout(() => setPhase('settle'), 1280),
			setTimeout(() => markParked(flight.id), 1760),
		];

		return () => {
			timers.forEach(clearTimeout);
		};
	}, [flight.id, markParked, shouldReduceMotion]);

	return (
		<Box
			position="fixed"
			inset={0}
			pointerEvents="none"
			zIndex={phase === 'hidden' || phase === 'peek' || phase === 'wave' ? BIZY_FLIGHT_PEEK_Z_INDEX : BIZY_FLIGHT_Z_INDEX}
		>
			<motion.div
				key={flight.id}
				initial={shouldReduceMotion ? { ...getPeekEntranceAnimation('reducedMotion', points), opacity: 0 } : getPeekEntranceAnimation('hidden', points)}
				animate={getPeekEntranceAnimation(phase, points)}
				transition={getPeekEntranceTransition(phase)}
				style={{
					position: 'absolute',
					width: BIZY_FLIGHT_SIZE,
					height: BIZY_FLIGHT_SIZE,
					willChange: 'transform, opacity, clip-path',
				}}
			>
				<motion.div
					animate={getPeekEntranceBizyAnimation(phase)}
					transition={getPeekEntranceTransition(phase)}
					style={{ width: BIZY_FLIGHT_SIZE, height: BIZY_FLIGHT_SIZE, transformOrigin: 'left center' }}
				>
					<Bizy state={getPeekEntranceBizyState(phase, flight.parkedState)} size={BIZY_FLIGHT_SIZE} />
				</motion.div>
			</motion.div>
		</Box>
	);
};
